import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Package } from "lucide-react";
import { CONFIG, waLink } from "./config";
import { Footer, Header } from "./components/layout";
import { Chapter, Reveal } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_STEPS = ["placed", "confirmed", "packed", "shipped", "delivered"];

export default function TrackOrder() {
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [step, setStep] = useState("mobile"); // mobile | otp | orders
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/otp/config`).then(({ data }) => setOtpEnabled(data.enabled)).catch(() => {});
    axios
      .get(`${API}/auth/me`)
      .then(() => loadOrders())
      .catch(() => {});
  }, []);

  const loadOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders/track`);
      setOrders(data);
      setStep("orders");
    } catch {
      setStep("mobile");
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/otp/send`, { mobile });
      toast.success("OTP sent to your mobile");
      setStep("otp");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/otp/verify`, { mobile, code });
      toast.success("Logged in");
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wrong OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="inner-page">
        <Chapter index="—" eyebrow="YOUR ORDERS" title={<>Track your <em>order</em></>} text="Verify your mobile number to see your orders and their live status." />
        {step === "mobile" && (
          <Reveal>
            <form data-testid="track-mobile-form" className="enquiry-form admin-login" onSubmit={sendOtp}>
              <label>
                Mobile number used while ordering
                <input data-testid="track-mobile-input" required type="tel" pattern="[0-9+\-\s]{10,20}" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile number" />
              </label>
              <button data-testid="track-send-otp" className="button primary" disabled={loading || !otpEnabled}>
                {loading ? "Sending…" : "Send OTP"} <ArrowRight size={16} />
              </button>
              {!otpEnabled && (
                <p className="catalogue-note" data-testid="otp-offline-note">
                  OTP login is being activated. Till then, message us on WhatsApp with your order reference for status.
                </p>
              )}
              <a data-testid="track-whatsapp-link" className="text-link" href={waLink("my order status")} target="_blank" rel="noreferrer">
                Ask on WhatsApp instead <ArrowRight size={14} />
              </a>
            </form>
          </Reveal>
        )}
        {step === "otp" && (
          <Reveal>
            <form data-testid="track-otp-form" className="enquiry-form admin-login" onSubmit={verifyOtp}>
              <label>
                Enter OTP sent to {mobile}
                <input data-testid="track-otp-input" required minLength={4} value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit OTP" inputMode="numeric" />
              </label>
              <button data-testid="track-verify-otp" className="button primary" disabled={loading}>
                {loading ? "Verifying…" : "Verify & view orders"} <ArrowRight size={16} />
              </button>
            </form>
          </Reveal>
        )}
        {step === "orders" && (
          <div className="admin-product-list">
            {orders.map((o) => {
              const idx = STATUS_STEPS.indexOf(o.status);
              return (
                <Reveal key={o.ref}>
                  <div className="admin-order" data-testid={`track-order-${o.ref}`}>
                    <div className="admin-order-head">
                      <b>Order #{o.ref}</b>
                      <span className={`order-status current`}>{o.status.toUpperCase()}</span>
                      {o.payment_status === "paid" && <span className="order-status paid">PAID</span>}
                    </div>
                    <p>{o.items.map((i) => `${i.name} ×${i.qty}${[i.size, i.color].filter(Boolean).length ? ` (${[i.size, i.color].filter(Boolean).join(" / ")})` : ""}`).join(", ")}</p>
                    <p className="order-meta">₹{o.total.toLocaleString("en-IN")} · {o.delivery_type} · {new Date(o.created_at).toLocaleString("en-IN")}</p>
                    <div className="status-track" data-testid={`status-track-${o.ref}`}>
                      {STATUS_STEPS.map((s, i) => (
                        <span key={s} className={`track-step ${i <= idx ? "done" : ""}`}>
                          <Package size={13} />
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
            {!orders.length && <p className="catalogue-note" data-testid="no-orders-note">No orders found for this number yet.</p>}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
